import { useMemo } from 'react';
import { LocationTaskStats } from '@weave/schema-gen-ts/dist/schemas/insys/onboarding/v2/onboarding-tasks/onboarding_tasks_v2.pb';
import { OnboardingTasksV2 } from '@frontend/api-onboarding-tasks';
import { PlatformLocationFeatureV1 } from '@frontend/api-platform-location-feature';
import { OnboardingCoreHooks } from '@frontend/onboarding-core';
import { useAppFlagStore } from '@frontend/shared';
import { ONBOARDING_DASHBOARD_FF } from '../constants';

const CACHE_TIME_MS = 1000 * 60 * 5; // 5 minutes

const DEFAULT_LOCATION_IDS: string[] = [];
const DEFAULT_LOCATION_TASK_STATS: Record<string, LocationTaskStats> = {};

type UseMultiOnboardingDashboardConfigProps = {
  locationIds?: string[];
  fetchOnboardingLocationsConfig?: boolean;
};

const checkOnboardingTasksUnskipped = (
  response: PlatformLocationFeatureV1.Types.CheckLocationNotificationPreferenceIO['output']
): boolean => {
  const { currentStatus } = response;
  return currentStatus === PlatformLocationFeatureV1.Types.Action.ACTION_UNKNOWN;
};

export const useMultiOnboardingDashboardConfig = ({
  locationIds = DEFAULT_LOCATION_IDS,
  fetchOnboardingLocationsConfig = true,
}: UseMultiOnboardingDashboardConfigProps = {}) => {
  // Step 1: Get fast-pass eligible locations from hierarchy config
  const { fastPassIntakeFormLocationsConfig, isLoading: isLoadingLocationHierarchyConfig } =
    OnboardingCoreHooks.useGetLocationHierarchyConfig({
      enabled: fetchOnboardingLocationsConfig,
      prepareIntakeFormConfig: true,
    });

  const selectedLocationIds = useMemo(
    () =>
      fetchOnboardingLocationsConfig ? fastPassIntakeFormLocationsConfig?.map((loc) => loc.locationId) : locationIds,
    [fetchOnboardingLocationsConfig, fastPassIntakeFormLocationsConfig, locationIds]
  );

  // Step 2: Filter locations with onboarding dashboard feature flag enabled
  const { getFeatureFlagLocationMap } = useAppFlagStore();

  const featureFlagEnabledLocationIds = useMemo(() => {
    if (!selectedLocationIds) return [];

    const featureFlagLocationMap = getFeatureFlagLocationMap({
      locationIds: selectedLocationIds,
      flagName: ONBOARDING_DASHBOARD_FF,
    });

    return selectedLocationIds.filter((locationId) => featureFlagLocationMap[locationId]?.value);
  }, [selectedLocationIds, getFeatureFlagLocationMap]);

  // Step 3: Check notification preferences to find locations that haven't skipped onboarding
  const hasEnabledLocations = featureFlagEnabledLocationIds.length > 0;

  const { data: activeOnboardingLocationIds, isLoading: isLoadingUnskippedLocations } =
    PlatformLocationFeatureV1.Queries.useCheckMultiLocationNotificationPreferencesQuery({
      request: {
        locationRequests: featureFlagEnabledLocationIds.map((locationId) => ({
          locationId,
          notificationEnum:
            PlatformLocationFeatureV1.Types.NotificationFeature.NOTIFICATION_FEATURE_ONBOARDING_TASKS_V2,
          sourceEnum: PlatformLocationFeatureV1.Types.Source.SOURCE_DASHBOARD_ONBOARDING_TASK,
        })),
      },
      options: {
        enabled: hasEnabledLocations,
        retry: false,
        select: (response) => {
          if (!response.locationPreferences) return [];
          return featureFlagEnabledLocationIds.filter(
            (locationId) =>
              !!response.locationPreferences && checkOnboardingTasksUnskipped(response.locationPreferences[locationId])
          );
        },
      },
    });

  // Step 4: Fetch module task stats for active onboarding locations
  const hasActiveLocations = !!(activeOnboardingLocationIds && activeOnboardingLocationIds.length > 0);

  const { data: locationWiseTaskStats = DEFAULT_LOCATION_TASK_STATS, isLoading: isLoadingTasksStats } =
    OnboardingTasksV2.Queries.useGetLocationTaskStatsForMultiQuery({
      request: {
        locationIds: activeOnboardingLocationIds ?? [],
      },
      options: {
        enabled: hasActiveLocations,
        retry: false,
        cacheTime: CACHE_TIME_MS,
        staleTime: CACHE_TIME_MS,
        select: (response) => response.locationStats,
      },
    });

  // Step 5: Compute final loading and eligibility states
  const isLoading = isLoadingLocationHierarchyConfig || isLoadingUnskippedLocations || isLoadingTasksStats;

  const computedStats = useMemo(() => {
    if (isLoading || !activeOnboardingLocationIds || !locationWiseTaskStats) {
      return {
        showOnboardingDashboard: false,
        locationsWithOnboardingTasks: [],
        eligibleLocationsForOnboardingRedirection: [],
        isOnboardingDashboardRedirectionEligible: false,
      };
    }
    const locationsWithOnboardingTasks: string[] = [];
    const eligibleLocationsForOnboardingRedirection: string[] = [];
    let showOnboardingDashboard = false;

    for (const locationId of activeOnboardingLocationIds) {
      const stats = locationWiseTaskStats[locationId];
      if (!stats || stats.tasksDurationExpired) continue;

      locationsWithOnboardingTasks.push(locationId);

      const hasIncompleteTasks = stats.completed !== stats.total;
      if (hasIncompleteTasks) {
        showOnboardingDashboard = true;

        if (!stats.enableSkipTasks) {
          eligibleLocationsForOnboardingRedirection.push(locationId);
        }
      }
    }

    return {
      locationsWithOnboardingTasks,
      eligibleLocationsForOnboardingRedirection,
      showOnboardingDashboard,
      isOnboardingDashboardRedirectionEligible: eligibleLocationsForOnboardingRedirection.length > 0,
    };
  }, [activeOnboardingLocationIds, isLoading, locationWiseTaskStats]);

  return {
    isLoading,
    showOnboardingDashboard: computedStats.showOnboardingDashboard,
    locationsWithOnboardingTasks: computedStats.locationsWithOnboardingTasks,
    isOnboardingDashboardRedirectionEligible: computedStats.isOnboardingDashboardRedirectionEligible,
    locationWiseTaskStats,
  };
};
